import { TextField } from '@mui/material';
import React, { useState } from "react";
import Rodape from "../rodape.js";
import { Header } from '../header.js';
import './account_common.css';

const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

const Recover = () => {
  const [stage, setStage] = useState(1); // 1 = email stage, 2 = code/password stage, 3 = success stage
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [error, setError] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [loading, setLoading] = useState(false);

  const [isMobile, setIsMobile] = React.useState(false);

  // Check device layout visibility
  const checkIfMobile = () => {
    const isMobileDevice = window.matchMedia('(max-width: 768px)').matches;
    setIsMobile(isMobileDevice);
  };

  React.useEffect(() => {
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const isEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const isNumericCode = (value) => {
    return /^\d{6}$/.test(value);
  };

  // Step 1: Request recovery code from custom backend
  const sendCode = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    if (!isEmail(email)) {
      setError("Erro: Por favor, insira um e-mail válido.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${baseAPI_URL}/users/recovery-code/${encodeURIComponent(email.toLowerCase().trim())}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();

      if (response.ok) {
        setStage(2);
      } else {
        setError(data.message || "Erro ao enviar o código. Verifique se o e-mail está cadastrado.");
      }
    } catch (err) {
      console.error("Erro ao enviar código:", err);
      setError("Erro de conexão com o servidor. Tente novamente mais tarde.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Submit code and new password to custom backend
  const resetPassword = async (event) => {
    event.preventDefault();
    setError("");
    setCodeError(false);

    if (isEmail(code)) {
      setError("Erro: Digite o código de verificação enviado por e-mail, não o seu endereço de e-mail.");
      setCodeError(true);
      return;
    }

    if (!isNumericCode(code)) {
      setError("Erro: Código inválido. Digite apenas os 6 números recebidos.");
      setCodeError(true);
      return;
    }

    if (password.length < 8) {
      setError("Erro: A nova senha deve ter no mínimo 8 caracteres.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Erro: As senhas são diferentes.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${baseAPI_URL}/users/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          code: code,
          new_password: password
        })
      });

      const data = await response.json();

      if (response.ok) {
        setStage(3);
      } else {
        setError(data.message || "Não foi possível alterar a senha. Código inválido ou expirado.");
        setCodeError(true);
      }
    } catch (err) {
      console.error("Erro ao resetar senha:", err);
      setError("Erro inesperado. Não foi possível alterar a senha. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header isMobile={isMobile} />
      <div className="auth-container-common">
        
        {stage === 1 && (
          <>
            <h2>Recuperação de Senha</h2>
            <p className="recover-subtitle">Insira seu e-mail cadastrado para enviarmos um código de 6 dígitos.</p>
            
            <div className="form form-stage-spacing">
              <div className="formfield input-field-spacing">
                <TextField
                  size="small"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  label="E-mail usado no site"
                  fullWidth
                  disabled={loading}
                />
              </div>
              
              <div className="recovery-notice-box">
                <h4>Por que enviamos um código?</h4>
                <p>Precisamos validar a titularidade da conta antes de habilitar a redefinição de dados sensíveis. O token numérico de 6 dígitos substitui links estáticos inseguros.</p>
              </div>

              <p className="avisosobresenha">No próximo passo, você informará esse código junto com sua nova senha definitiva.</p>
              
              {error && <p className="error-password">{error}</p>}
              
              <div className="formfield button-field-spacing">
                <button 
                  type="submit" 
                  className="btn-account" 
                  onClick={sendCode}
                  disabled={loading}
                >
                  {loading ? "Enviando..." : "Enviar código de verificação"}
                </button>
              </div>
              
              <div className="recovery-footer-links">
                <a href="/logon">← Voltar para a tela de login</a>
              </div>
            </div>
          </>
        )}

        {stage === 2 && (
          <>
            <h2>Escolha da nova senha</h2>
            <p className="recover-subtitle">Preencha o código recebido no email <strong>{email}</strong> e escolha sua nova senha.</p>
            
            <div className="form form-stage-spacing">
              <div className="formfield input-field-spacing">
                <TextField
                  size="small"
                  value={code}
                  onChange={event => setCode(event.target.value)}
                  label="Código de verificação (6 dígitos)"
                  fullWidth
                  autoComplete="one-time-code"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                  error={codeError}
                  disabled={loading}
                />
              </div>
              
              <div className="formfield input-field-spacing">
                <TextField
                  size="small"
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  type="password"
                  label="Nova senha de acesso"
                  fullWidth
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>
              
              <div className="formfield input-field-spacing">
                <TextField
                  size="small"
                  value={confirmPassword}
                  onChange={event => setConfirmPassword(event.target.value)}
                  type="password"
                  label="Confirme sua nova senha"
                  fullWidth
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>

              <div className="password-policy-box">
                <h4>Requisitos:</h4>
                <ul>
                  <li>Mínimo de 8 caracteres.</li>
                  <li>Recomendado usar letras maiúsculas, números e caracteres especiais para maior proteção.</li>
                </ul>
              </div>

              {error && <p className="error-password">{error}</p>}
              
              <div className="formfield button-field-spacing">
                <button 
                  type="submit" 
                  className="btn-account" 
                  onClick={resetPassword}
                  disabled={loading}
                >
                  {loading ? "Processando..." : "Confirmar e alterar senha"}
                </button>
              </div>

              <div className="recovery-footer-links">
                <button 
                  type="button" 
                  className="btn-link-style" 
                  onClick={() => { setStage(1); setError(""); }}
                >
                  ← Corrigir e-mail informado
                </button>
              </div>
            </div>
          </>
        )}

        {stage === 3 && (
          <>
            <h2>Senha alterada com sucesso!</h2>
            
            <div className="form form-success-spacing">
              <div className="success-info-box">
                <h4>Próximos passos recomendados:</h4>
                <ul>
                  <li>Utilize seu e-mail e a nova senha cadastrada para entrar.</li>
                  <li>Caso seu navegador tenha salvo a senha antiga, lembre-se de atualizá-la no gerenciador.</li>
                  <li>Se você utiliza múltiplos dispositivos, será necessário fazer o login novamente neles.</li>
                </ul>
              </div>

              <p className="avisosobresenha success-button-instruction">
                Clique no botão abaixo para ir direto à tela de autenticação e iniciar seus estudos:
              </p>

              <div className="formfield success-button-field">
                <button 
                  type="button" 
                  className="btn-account" 
                  onClick={() => window.location.href = '/logon'}
                >
                  Acessar página de login
                </button>
              </div> 

              <hr className="success-divider" />
              
              <p className="success-support-text">
                Ainda está com problemas para acessar? Entre em contato com o nosso suporte.
              </p>
            </div>
          </>
        )}
      </div>
      <Rodape />
    </>
  );
};

export default Recover;